import { Alert, Button, Card, Col, DatePicker, Form, Pagination, Row, Select, Space, Table } from 'antd'
import type { SorterResult, SortOrder } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { SavedReportBean, TrackerReportKeyColumnDescription, TrackerReportValueColumnDescription } from 'src/api/api'
import { downloadCSVData, enumView, lowerize } from 'src/util/Util'
import TrackerReportFilters, { Filter } from './TrackerReportFilters'
import trackerReportApi from './TrackerReportApi'
import '../report/style.css'
import '../formSortableSelect/formSortableSelect.css'
import { FormSortableSelect } from '../formSortableSelect/FormSortableSelect'
import { useReportPolling } from '../report/useReportPolling'
import { buildReportColumns } from './trackerReportRequest'
import {
    calculateColumnsWidth,
    formatNumberWithGrouping,
    isIdColumn,
    reportPeriodLabels,
    toNumberIfPossible
} from '../report/columnsHelper'

const FormItem = Form.Item
const Option = Select.Option
const KeyColumns = TrackerReportKeyColumnDescription.ColumnEnum
const ValueColumns = TrackerReportValueColumnDescription.ColumnEnum

type SortState = { columnKey: string; order?: SortOrder } | null

export const TrackerReport: React.FC = () => {
    const [error, setError] = useState<string | undefined>(undefined)
    const [form] = Form.useForm()

    const [filters, setFilters] = useState<Filter[]>([])

    const normalizeKey = (v: any) => String(v ?? '').replace(/\s+/g, '').toLowerCase()

    const [sortState, setSortState] = useState<SortState>({ columnKey: normalizeKey('Day Hour'), order: 'ascend' })

    const nowDate = useMemo(() => dayjs(), [])

    const keyColumnSet = useMemo(() => new Set(Object.values(KeyColumns as any) as any[]), [])
    const valueColumnSet = useMemo(() => new Set(Object.values(ValueColumns as any) as any[]), [])

    const isKeyColumn = (col: string) => keyColumnSet.has(col as any)
    const isValueColumn = (col: string) => valueColumnSet.has(col as any)

    const requestReport = async (sorter: SorterResult<any> | undefined) => {

        setError(undefined)
        const values = await form.validateFields()

        const keys = (values.keys as string[]) || []
        const columns = (values.columns as string[]) || []
        const allColumns = ([] as string[]).concat(keys, columns)

        const { keyColumns, valueColumns } = buildReportColumns({
            formValues: values,
            allColumnsInOrder: allColumns,
            filters,
            sorter,
            now: nowDate,
            isKeyColumn,
            isValueColumn,
        })

        try {
            const created = await trackerReportApi.createReport({ keyColumns, valueColumns })
            return created.data
        } catch (e) {
            const msg = await (e as any).json()
            setError(msg.error)
            return undefined
        }
    }

    const { runReport, downloadCsv, loadPage, state, setError: setPollingError, reset } = useReportPolling({
        api: trackerReportApi,
        downloadCSVData,
        requestReport,
        pollMs: 500,
    })

    useEffect(() => {
        setPollingError(error)
    }, [error, setPollingError])

    const headerFunc = (headersToProcess?: any[]) => {
        return headersToProcess?.map((header) => {
            const key = normalizeKey(header)
            const active = sortState?.columnKey === key

            return {
                title: header,
                dataIndex: String(header).toLowerCase(),
                key,
                sorter: true,
                sortOrder: active ? sortState?.order : undefined,
                sortDirections: ['ascend', 'descend'] as SortOrder[],
                render: (value: any) => {
                    if (isIdColumn(header)) return value
                    const n = toNumberIfPossible(value)
                    return n !== null ? formatNumberWithGrouping(n) : value
                },
            }
        })
    }

    const sourceFunc = (dataToProcess?: any[], headersToProcess?: any[], totalsToProcess?: any) => {
        const source =
            dataToProcess?.map(
                (row, rowIndex) =>
                    headersToProcess?.reduce(
                        (prev, current, columnIndex) => ({
                            ...prev,
                            [current.toLowerCase() ?? '']: row[columnIndex],
                            key: rowIndex,
                        }),
                        {},
                    ) ?? {},
            ) ?? []

        totalsToProcess && source?.push(lowerize(totalsToProcess))
        return source
    }

    const renderPagination = () => {
        return state.totalLines ? (
            <Pagination current={state.currentPage} onChange={loadPage} pageSize={state.pageSize}
                total={state.totalLines} />
        ) : null
    }

    const period = Form.useWatch('period', form) as SavedReportBean.ReportPeriodEnum | undefined

    const rawColumns = useMemo(() => headerFunc(state.headers) ?? [], [state.headers, sortState])
    const rawSource = useMemo(() => sourceFunc(state.data, state.headers, state.totals) ?? [], [state.data, state.headers, state.totals])

    const { tableWidth } = useMemo(() => {
        return calculateColumnsWidth(rawColumns, rawSource, 500)
    }, [rawColumns, rawSource])

    useEffect(() => {
        form.resetFields()
        setFilters([])
        setError(undefined)

        reset()

        setSortState({ columnKey: 'dayhour', order: 'ascend' })

        form.setFieldsValue({
            period: SavedReportBean.ReportPeriodEnum.TODAY,
            keys: [],
            columns: [],
            startDate: undefined,
            endDate: undefined,
        })
    }, [form, reset])

    const keyOptions = useMemo(
        () =>
            Object.keys(KeyColumns)
                .sort()
                .map((key) => ({
                    label: enumView(key),
                    value: key,
                })),
        [],
    )

    const metricOptions = useMemo(
        () =>
            Object.keys(ValueColumns)
                .sort()
                .map((value) => ({
                    label: enumView(value),
                    value,
                })),
        [],
    )

    return (
        <div>
            <Form
                style={{ maxWidth: '1000px', margin: '0 auto' }}
                onFinish={() => runReport(undefined)}
                form={form}
                labelCol={{ span: 3 }}
                wrapperCol={{ span: 21 }}
            >
                {error && <Alert message={error} type="error" />}

                <Card size="small" title="Tracker Report Builder" className="form-card">
                    <FormItem label="Period" name="period" rules={[{ required: true, message: 'Select period' }]}>
                        <Select
                            allowClear
                            placeholder="Select period"
                            value={period}
                            onChange={(v) => form.setFieldValue('period', v)}
                        >
                            {Object.values(SavedReportBean.ReportPeriodEnum)
                                .filter((v) => v in reportPeriodLabels)
                                .map((p) => (
                                    <Option key={p} value={p} title={reportPeriodLabels[p] ?? p}>
                                        {reportPeriodLabels[p] ?? p}
                                    </Option>
                                ))}
                        </Select>
                    </FormItem>

                    {period === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE && (
                        <FormItem label=" " colon={false}>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 20px 1fr',
                                    columnGap: '10px',
                                }}
                            >
                                <Form.Item
                                    name="startDate"
                                    style={{ marginBottom: 0 }}
                                    dependencies={['period', 'endDate']}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (getFieldValue('period') !== SavedReportBean.ReportPeriodEnum.CUSTOMRANGE) {
                                                    return Promise.resolve()
                                                }
                                                return value ? Promise.resolve() : Promise.reject(new Error('Please select start date'))
                                            },
                                        }),
                                    ]}
                                >
                                    <DatePicker
                                        placeholder="Start Date"
                                        style={{ width: '100%' }}
                                        disabledDate={(date) =>
                                            !!form.getFieldValue('endDate') && date.isAfter(form.getFieldValue('endDate'), 'day')
                                        }
                                    />
                                </Form.Item>

                                <span style={{ display: 'flex', justifyContent: 'center', lineHeight: '32px' }}>-</span>

                                <Form.Item
                                    name="endDate"
                                    style={{ marginBottom: 0 }}
                                    dependencies={['period', 'startDate']}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (getFieldValue('period') !== SavedReportBean.ReportPeriodEnum.CUSTOMRANGE) {
                                                    return Promise.resolve()
                                                }
                                                return value ? Promise.resolve() : Promise.reject(new Error('Please select end date'))
                                            },
                                        }),
                                    ]}
                                >
                                    <DatePicker
                                        placeholder="End Date"
                                        style={{ width: '100%' }}
                                        disabledDate={(date) =>
                                            !!form.getFieldValue('startDate') && date.isBefore(form.getFieldValue('startDate'), 'day')
                                        }
                                    />
                                </Form.Item>
                            </div>
                        </FormItem>
                    )}

                    <FormItem name="keys" label="Dimensions">
                        <FormSortableSelect
                            value={(Form.useWatch('keys', form) as string[]) ?? []}
                            onChange={(v) => form.setFieldsValue({ keys: v })}
                            placeholder="Select dimensions"
                            options={keyOptions}
                            selectProps={{ showSearch: true }}
                        />
                    </FormItem>

                    <FormItem
                        name="columns"
                        label="Metrics"
                        rules={[{ required: true, message: 'Select at least one metric' }]}
                    >
                        <FormSortableSelect
                            value={(Form.useWatch('columns', form) as string[]) ?? []}
                            onChange={(v) => form.setFieldsValue({ columns: v })}
                            placeholder="Select metrics"
                            options={metricOptions}
                            selectProps={{ showSearch: true }}
                        />
                    </FormItem>

                    <TrackerReportFilters filters={filters} setFilters={setFilters} />

                    <FormItem wrapperCol={{ offset: 3, span: 21 }}>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={state.loading}>
                                Generate report
                            </Button>
                            <Button type="default" htmlType="button" loading={state.loading} onClick={downloadCsv}>
                                Download CSV
                            </Button>
                        </Space>
                    </FormItem>
                </Card>
            </Form>

            {state.data && (
                <Table
                    pagination={false}
                    columns={headerFunc(state.headers)}
                    dataSource={sourceFunc(state.data, state.headers, state.totals)}
                    rowKey="key"
                    scroll={{ x: tableWidth }}
                    onChange={(p, f, sorter: SorterResult<any>) => {

                        const incomingKeyRaw = (sorter as any).columnKey ?? (sorter as any).field
                        const incomingKey = typeof incomingKeyRaw === 'string' ? normalizeKey(incomingKeyRaw) : ''
                        const effectiveKey = incomingKey || sortState?.columnKey || 'dayhour'

                        const nextOrder = sorter.order as SortOrder | undefined

                        setSortState(effectiveKey ? { columnKey: effectiveKey, order: nextOrder } : null)

                        if (!nextOrder) {
                            runReport(undefined)
                            return
                        }

                        const headerForTitle = (state.headers ?? []).find((h) => normalizeKey(h) === effectiveKey)
                        const nextSorter: SorterResult<any> = {
                            ...sorter,
                            order: nextOrder,
                            column: (sorter as any).column ?? (headerForTitle ? ({ title: headerForTitle } as any) : undefined),
                        } as any

                        runReport(nextSorter)
                    }}
                />
            )}

            <Row>
                <Col span={24} style={{ display: 'flex', justifyContent: 'center', marginTop: '1%' }}>
                    {renderPagination()}
                </Col>
            </Row>
        </div>
    )
}



// WEBPACK FOOTER //
// ./src/component/app/trackerReport/TrackerReport.tsx